import { io } from 'socket.io-client';
import { store } from '../store/store';
import toast from 'react-hot-toast';
import {
  addNotification,
  setSocketConnected,
  setSocketError
} from '../store/slices/notificationSlice';
import { notificationApi } from '../services/notificationApi';
import { loanApi } from '../services/loanApi';
import { taskApi } from '../services/taskApi';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.notificationContext = null; // optional context hooks
  }

  // Optional: allow UI contexts to attach handlers (kept no-op to avoid duplicate storage)
  setNotificationContext(ctx) {
    this.notificationContext = ctx;
  }

  connect() {
    if (this.socket?.connected) {
      console.log('🔌 Socket already connected, skipping...');
      return;
    }

    const state = store.getState();
    const token = state.auth.token;
    const user = state.auth.user;

    console.log('🔌 Attempting socket connection with auth state:', {
      hasToken: !!token,
      hasUser: !!user,
      isAuthenticated: state.auth.isAuthenticated,
      initialized: state.auth.initialized
    });

    if (!token || !user) {
      console.warn('❌ No auth token or user found, skipping socket connection');
      store.dispatch(setSocketError('Authentication required'));
      return;
    }

    try {
      console.log('✅ Connecting to socket server...');

      this.socket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000', {
        auth: {
          token
        },
        transports: ['websocket', 'polling']
      });

      this.setupEventListeners();
      this.setupReconnection();
    } catch (error) {
      console.error('❌ Failed to create socket connection:', error);
      store.dispatch(setSocketError(error.message));
    }
  }

  setupEventListeners() {
    this.socket.on('connect', () => {
      console.log('✅ Connected to socket server');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      store.dispatch(setSocketConnected(true));

      // The server will automatically join rooms based on authenticated user
      // No need to manually emit join events
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Disconnected from socket server:', reason);
      this.isConnected = false;
      store.dispatch(setSocketConnected(false));
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      this.isConnected = false;
      store.dispatch(setSocketError(error.message));
    });

    // Leave request notifications
    this.socket.on('leave:new-request', (data) => {
      console.log('📨 Received leave:new-request event:', data);
      this.handleNewLeaveRequest(data);
    });

    this.socket.on('leave:request-status-changed', (data) => {
      console.log('📨 Received leave:request-status-changed event:', data);
      this.handleLeaveRequestStatusChanged(data);
    });

    this.socket.on('leave:request-approved', (data) => {
      console.log('📨 Received leave:request-approved event:', data);
      this.handleLeaveApproved(data);
    });

    this.socket.on('leave:request-rejected', (data) => {
      console.log('📨 Received leave:request-rejected event:', data);
      this.handleLeaveRejected(data);
    });

    this.socket.on('leave:request-cancelled', (data) => {
      this.handleLeaveCancelled(data);
    });

    // Attendance notifications
    this.socket.on('attendance:user-clocked-in', (data) => {
      this.handleAttendanceClockIn(data);
    });

    this.socket.on('attendance:user-clocked-out', (data) => {
      this.handleAttendanceClockOut(data);
    });

    // Holiday notifications
    this.socket.on('holiday:new-holiday', (data) => {
      this.handleNewHoliday(data);
    });

    this.socket.on('holiday:holiday-updated', (data) => {
      this.handleHolidayUpdated(data);
    });

    // Payroll notifications
    this.socket.on('payroll:salary-slip-available', (data) => {
      console.log('📨 Received payroll:salary-slip-available event:', data);
      this.handleSalarySlipAvailable(data);
    });

    this.socket.on('payroll:salary-slip-ready', (data) => {
      console.log('📨 Received payroll:salary-slip-ready event:', data);
      this.handleSalarySlipReady(data);
    });

    // Payment notifications
    this.socket.on('payment:verified', (data) => {
      console.log('📨 Received payment:verified event:', data);
      this.handlePaymentVerified(data);
    });

    this.socket.on('payment:rejected', (data) => {
      console.log('📨 Received payment:rejected event:', data);
      this.handlePaymentRejected(data);
    });

    this.socket.on('payment:upgrade-request', (data) => {
      console.log('📨 Received payment:upgrade-request event:', data);
      this.handlePaymentUpgradeRequest(data);
    });

    // New payment submission notification for super admins
    this.socket.on('payment:new-submission', (data) => {
      console.log('📨 Received payment:new-submission event:', data);
      this.handleNewPaymentSubmission(data);
    });

    // Loan request notifications
    this.socket.on('loan:new-request', (data) => {
      console.log('📨 Received loan:new-request event:', data);
      this.handleNewLoanRequest(data);
    });

    this.socket.on('loan:request-approved', (data) => {
      console.log('📨 Received loan:request-approved event:', data);
      this.handleLoanApproved(data);
    });

    this.socket.on('loan:request-rejected', (data) => {
      console.log('📨 Received loan:request-rejected event:', data);
      this.handleLoanRejected(data);
    });

    this.socket.on('loan:request-cancelled', (data) => {
      console.log('📨 Received loan:request-cancelled event:', data);
      this.handleLoanCancelled(data);
    });

    // Asset notifications
    this.socket.on('asset:new-request', (data) => {
      console.log('📨 Received asset:new-request event:', data);
      this.handleNewAssetRequest(data);
    });

    this.socket.on('asset:assigned', (data) => {
      console.log('📨 Received asset:assigned event:', data);
      this.handleAssetAssigned(data);
    });

    this.socket.on('asset:returned', (data) => {
      console.log('📨 Received asset:returned event:', data);
      this.handleAssetReturned(data);
    });

    this.socket.on('asset:request-approved', (data) => {
      console.log('📨 Received asset:request-approved event:', data);
      this.handleAssetRequestApproved(data);
    });

    this.socket.on('asset:request-rejected', (data) => {
      console.log('📨 Received asset:request-rejected event:', data);
      this.handleAssetRequestRejected(data);
    });

    // Task notifications
    this.socket.on('task:created', (data) => {
      console.log('📨 Client received task:created event:', data);
      this.handleTaskCreated(data);
    });

    this.socket.on('task:updated', (data) => {
      console.log('📨 Client received task:updated event:', data);
      this.handleTaskUpdated(data);
    });

    this.socket.on('task:comment-added', (data) => {
      console.log('📨 Client received task:comment-added event:', data);
      this.handleTaskCommentAdded(data);
    });

    this.socket.on('task:comment-updated', (data) => {
      console.log('📨 Client received task:comment-updated event:', data);
      this.handleTaskCommentUpdated(data);
    });

    this.socket.on('task:comment-deleted', (data) => {
      console.log('📨 Client received task:comment-deleted event:', data);
      this.handleTaskCommentDeleted(data);
    });
  }

  setupReconnection() {
    this.socket.on('disconnect', () => {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => {
          this.reconnectAttempts++;
          console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          this.socket.connect();
        }, 2000 * this.reconnectAttempts); // Exponential backoff
      }
    });
  }

  // Notification handlers
  handleNewLeaveRequest(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'New Leave Request',
        message: `${data.employeeName} has submitted a leave request for ${data.workingDays || 1} days`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for new leave request');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Also invalidate leave requests cache to automatically refetch the admin list
      import('./leaveApi').then(({ leaveApi }) => {
        store.dispatch(leaveApi.util.invalidateTags(['Leave']));
        console.log('✅ Leave requests cache invalidated - admin list should refresh');
      });

      console.log('✅ Cache invalidated for new leave request');

      // Show toast notification
      toast.success(`New leave request from ${data.employeeName}`, {
        duration: 5000,
        icon: '📋',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleLeaveRequestStatusChanged(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      console.log('🔄 Invalidating leave requests cache for status change');

      // Invalidate leave requests cache to automatically refetch the admin list
      import('./leaveApi').then(({ leaveApi }) => {
        store.dispatch(leaveApi.util.invalidateTags(['Leave']));
        console.log('✅ Leave requests cache invalidated after status change - admin list should refresh');
      });

      // Show toast notification about the status change
      const statusMessage = data.changeType === 'approved' ? 'approved' : 'rejected';
      toast.success(`Leave request for ${data.employeeName} has been ${statusMessage}`, {
        duration: 4000,
        icon: '📋',
        style: {
          background: data.changeType === 'approved' ? '#10B981' : '#EF4444',
          color: '#fff',
        },
      });
    }
  }

  handleLeaveApproved(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleLeaveApproved called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'success',
        title: 'Leave Request Approved',
        message: 'Your leave request has been approved!'
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast.success('Your leave request has been approved! 🎉', {
        duration: 6000,
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleLeaveRejected(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleLeaveRejected called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'error',
        title: 'Leave Request Rejected',
        message: `Your leave request has been rejected. ${data.remarks ? `Reason: ${data.remarks}` : ''}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast.error(`Your leave request has been rejected: ${data.remarks || 'No reason provided'}`, {
        duration: 8000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
    }
  }

  handleLeaveCancelled(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'warning',
        title: 'Leave Request Cancelled',
        message: `${data.employeeName} has cancelled their leave request`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast(`Leave request cancelled by ${data.employeeName}`, {
        duration: 5000,
        icon: '🚫',
        style: {
          background: '#F59E0B',
          color: '#fff',
        },
      });
    }
  }

  handleAttendanceClockIn(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'Employee Clocked In',
        message: `${data.employeeName} clocked in at ${new Date(data.timestamp).toLocaleTimeString()}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast(`${data.employeeName} clocked in at ${new Date(data.timestamp).toLocaleTimeString()}`, {
        duration: 3000,
        icon: '🕐',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      });
    }
  }

  handleAttendanceClockOut(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'Employee Clocked Out',
        message: `${data.employeeName} clocked out at ${new Date(data.timestamp).toLocaleTimeString()}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast(`${data.employeeName} clocked out at ${new Date(data.timestamp).toLocaleTimeString()}`, {
        duration: 3000,
        icon: '🏠',
        style: {
          background: '#8B5CF6',
          color: '#fff',
        },
      });
    }
  }

  handleNewHoliday(data) {
    // Add to Redux store
    store.dispatch(addNotification({
      type: 'success',
      title: 'New Holiday Added',
      message: `New holiday: ${data.title} on ${new Date(data.date).toLocaleDateString()}`
    }));

    // Invalidate RTK Query cache to trigger refetch
    store.dispatch(notificationApi.util.invalidateTags(['Notification']));

    // Show toast notification
    toast.success(`New holiday added: ${data.title}`, {
      duration: 5000,
      icon: '🎉',
      style: {
        background: '#10B981',
        color: '#fff',
      },
    });
  }

  handleHolidayUpdated(data) {
    // Add to Redux store
    store.dispatch(addNotification({
      type: 'warning',
      title: 'Holiday Updated',
      message: `Holiday updated: ${data.title}`
    }));

    // Invalidate RTK Query cache to trigger refetch
    store.dispatch(notificationApi.util.invalidateTags(['Notification']));

    // Show toast notification
    toast(`Holiday updated: ${data.title}`, {
      duration: 4000,
      icon: '📅',
      style: {
        background: '#F59E0B',
        color: '#fff',
      },
    });
  }

  handleSalarySlipAvailable(data) {
    const user = store.getState().auth.user;
    // This event is specifically for the current user
    if (user?.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'success',
        title: 'Salary Slip Available',
        message: `Your salary slip for ${data.payrollPeriod} is now available for download`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast.success(`💰 Salary slip for ${data.payrollPeriod} is ready!`, {
        duration: 8000,
        icon: '💰',
        style: {
          background: '#10B981',
          color: '#fff',
        },
        action: {
          label: 'View',
          onClick: () => {
            // Navigate to salary slip page (this could be improved with router navigation)
            window.location.href = '/employee/salary-slips';
          }
        }
      });
    }
  }

  handleSalarySlipReady(data) {
    const user = store.getState().auth.user;
    // This event is specifically for the current user
    if (user?.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'Salary Slip Ready',
        message: `Your salary slip for ${data.payrollPeriod} has been generated and is ready for download`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification
      toast(`📄 Salary slip for ${data.payrollPeriod} is ready for download!`, {
        duration: 6000,
        icon: '📄',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      });
    }
  }

  // Payment notification handlers
  handlePaymentVerified(data) {
    const user = store.getState().auth.user;
    // Only show to company admins of the relevant company
    if (user?.role === 'admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'success',
        title: 'Payment Verified ✅',
        message: `Your payment for ${data.planName} plan has been verified and your account has been activated.`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification', 'Subscription']));

      // Show toast notification
      toast.success(`Payment verified! Your ${data.planName} plan is now active! 🎉`, {
        duration: 8000,
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handlePaymentRejected(data) {
    const user = store.getState().auth.user;
    // Only show to company admins of the relevant company
    if (user?.role === 'admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'error',
        title: 'Payment Rejected ❌',
        message: `Your payment for ${data.planName} plan has been rejected. Please contact support for assistance.`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification', 'Subscription']));

      // Show toast notification
      toast.error(`Payment rejected: ${data.reason || 'Please contact support'}`, {
        duration: 10000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
    }
  }

  handlePaymentUpgradeRequest(data) {
    const user = store.getState().auth.user;
    // Only show to super admins
    if (user?.role === 'super_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'New Plan Upgrade Request 🔄',
        message: `${data.companyName} has requested to upgrade to ${data.planName} plan`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification', 'Payment']));

      // Show toast notification
      toast(`New upgrade request from ${data.companyName}`, {
        duration: 8000,
        icon: '🔄',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      });
    }
  }

  handleNewPaymentSubmission(data) {
    const user = store.getState().auth.user;
    // Only show to super admins for payment verification
    // Check if this event is targeted for super admins
    if (user?.role === 'super_admin' && (!data.targetRole || data.targetRole === 'super_admin')) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: '💳 New Payment Submitted',
        message: `${data.companyName} has submitted a payment for ${data.planName} plan (${data.amount})`
      }));

      // Import superAdminApi to invalidate payment cache
      import('../store/api/superAdminApi').then(({ superAdminApi }) => {
        // Invalidate RTK Query cache to trigger refetch of pending payments
        store.dispatch(superAdminApi.util.invalidateTags(['Payment']));
        console.log('✅ Payment cache invalidated - pending payments will refresh');
      });

      // Also invalidate notification cache
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Show toast notification with payment details
      toast.success(`New payment submission from ${data.companyName}`, {
        duration: 8000,
        icon: '💳',
        style: {
          background: '#059669',
          color: '#fff',
          borderLeft: '4px solid #10B981'
        },
      });

      // Show a follow-up notification with details
      setTimeout(() => {
        toast(`💰 Amount: ${data.amount} | Plan: ${data.planName}`, {
          duration: 6000,
          icon: '📋',
          style: {
            background: '#1E40AF',
            color: '#fff',
          },
        });
      }, 1000);
    }
  }

  // Loan notification handlers
  handleNewLoanRequest(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'New Loan Request',
        message: `${data.employeeName} has submitted a loan request for ₹${data.amount?.toLocaleString('en-IN')}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for new loan request');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));
      store.dispatch(loanApi.util.invalidateTags(['Loan']));
      console.log('✅ Cache invalidated for new loan request');

      // Show toast notification
      toast.success(`New loan request from ${data.employeeName}`, {
        duration: 5000,
        icon: '💰',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleLoanApproved(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleLoanApproved called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'success',
        title: 'Loan Request Approved',
        message: 'Your loan request has been approved! The amount will be processed according to your repayment plan.'
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));
      store.dispatch(loanApi.util.invalidateTags(['Loan']));

      // Show toast notification
      toast.success('Your loan request has been approved! 🎉', {
        duration: 6000,
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleLoanRejected(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleLoanRejected called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'error',
        title: 'Loan Request Rejected',
        message: `Your loan request has been rejected. ${data.remarks ? `Reason: ${data.remarks}` : ''}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));
      store.dispatch(loanApi.util.invalidateTags(['Loan']));

      // Show toast notification
      toast.error(`Your loan request has been rejected: ${data.remarks || 'No reason provided'}`, {
        duration: 8000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
    }
  }

  handleLoanCancelled(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'warning',
        title: 'Loan Request Cancelled',
        message: `${data.employeeName} has cancelled their loan request`
      }));

      // Invalidate RTK Query cache to trigger refetch
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));
      store.dispatch(loanApi.util.invalidateTags(['Loan']));

      // Show toast notification
      toast(`Loan request cancelled by ${data.employeeName}`, {
        duration: 5000,
        icon: '🚫',
        style: {
          background: '#F59E0B',
          color: '#fff',
        },
      });
    }
  }

  // Task notification handlers
  handleTaskCreated(data) {
    const user = store.getState().auth.user;
    // Show to admins and company admins
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'New Task Created',
        message: `New task "${data.title}" has been created`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for new task');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Invalidate task cache
      store.dispatch(taskApi.util.invalidateTags(['Tasks']));
      console.log('✅ Task cache invalidated for new task');

      // Show toast notification
      toast.success(`📋 New task created: ${data.title}`, {
        duration: 5000,
        icon: '📋',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleTaskUpdated(data) {
    const user = store.getState().auth.user;
    // Check if current user is assigned to the task or is the creator
    const isRelated = data.assignedTo.some(id => id.toString() === user?._id.toString()) ||
                     data.createdBy.toString() === user?._id.toString();

    if (isRelated) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: data.changeType === 'status_changed' ? 'warning' : 'info',
        title: 'Task Updated',
        message: `Task "${data.title}" has been updated`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for task updated');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Invalidate task cache - both list and specific task
      store.dispatch(taskApi.util.invalidateTags([
        'Tasks',
        { type: 'Task', id: data.taskId }
      ]));
      console.log('✅ Task cache invalidated for updated task');

      // Show toast notification
      const icon = data.changeType === 'status_changed' ? '🔄' : '📝';
      toast(`Task updated: ${data.title}`, {
        duration: 4000,
        icon: icon,
        style: {
          background: data.changeType === 'status_changed' ? '#F59E0B' : '#3B82F6',
          color: '#fff',
        },
      });
    }
  }

  handleTaskCommentAdded(data) {
    const user = store.getState().auth.user;
    // Check if current user is assigned to the task or is the task creator (but not the commenter)
    const isAssigned = data.assignedTo.some(id => id.toString() === user?._id.toString());
    const isCreator = data.createdBy && data.createdBy.toString() === user?._id.toString();
    const isCommenter = data.commenterId.toString() === user?._id.toString();

    if ((isAssigned || isCreator) && !isCommenter) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'New Comment on Task',
        message: `"${data.message}" - New comment on task: ${data.title}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for new task comment');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Invalidate task cache - both list and specific task
      store.dispatch(taskApi.util.invalidateTags([
        'Tasks',
        { type: 'Task', id: data.taskId }
      ]));
      console.log('✅ Task cache invalidated for new comment');

      // Show toast notification
      toast.success(`💬 New comment on task: ${data.title}`, {
        duration: 5000,
        icon: '💬',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleTaskCommentUpdated(data) {
    const user = store.getState().auth.user;
    // Check if current user is assigned to the task or is the task creator (but not the commenter)
    const isAssigned = data.assignedTo.some(id => id.toString() === user?._id.toString());
    const isCreator = data.createdBy && data.createdBy.toString() === user?._id.toString();
    const isCommenter = data.commenterId.toString() === user?._id.toString();

    if ((isAssigned || isCreator) && !isCommenter) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'Comment Updated on Task',
        message: `A comment was updated on task: ${data.title}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for updated task comment');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Invalidate task cache - both list and specific task
      store.dispatch(taskApi.util.invalidateTags([
        'Tasks',
        { type: 'Task', id: data.taskId }
      ]));
      console.log('✅ Task cache invalidated for updated comment');

      // Show toast notification
      toast(`💬 Comment updated on task: ${data.title}`, {
        duration: 4000,
        icon: '💬',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      });
    }
  }

  handleTaskCommentDeleted(data) {
    const user = store.getState().auth.user;
    // Check if current user is assigned to the task or is the task creator (but not the commenter)
    const isAssigned = data.assignedTo.some(id => id.toString() === user?._id.toString());
    const isCreator = data.createdBy && data.createdBy.toString() === user?._id.toString();
    const isCommenter = data.commenterId.toString() === user?._id.toString();

    if ((isAssigned || isCreator) && !isCommenter) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'Comment Deleted from Task',
        message: `A comment was deleted from task: ${data.title}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for deleted task comment');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Invalidate task cache - both list and specific task
      store.dispatch(taskApi.util.invalidateTags([
        'Tasks',
        { type: 'Task', id: data.taskId }
      ]));
      console.log('✅ Task cache invalidated for deleted comment');

      // Show toast notification
      toast(`💬 Comment deleted from task: ${data.title}`, {
        duration: 4000,
        icon: '💬',
        style: {
          background: '#6B7280',
          color: '#fff',
        },
      });
    }
  }

  // Asset notification handlers
  handleNewAssetRequest(data) {
    const user = store.getState().auth.user;
    if (user?.role === 'admin' || user?.role === 'super-admin' || user?.role === 'company_admin') {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'New Asset Request',
        message: `${data.employeeName} has submitted an asset request for ${data.assetType}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for new asset request');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Import assetApi to invalidate asset requests cache for admin
      import('../services/assetApi').then(({ assetApi }) => {
        store.dispatch(assetApi.util.invalidateTags(['AssetRequest']));
        console.log('✅ Asset request cache invalidated for admin');
      });

      // Show toast notification
      toast.success(`📦 New asset request from ${data.employeeName}`, {
        duration: 5000,
        icon: '📦',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleAssetAssigned(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleAssetAssigned called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'success',
        title: 'Asset Assigned',
        message: `Asset ${data.assetName || data.assetId} has been assigned to you`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for asset assigned');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Import assetApi to invalidate asset cache
      import('../services/assetApi').then(({ assetApi }) => {
        store.dispatch(assetApi.util.invalidateTags(['Asset']));
        console.log('✅ Asset cache invalidated for reassigned asset');
      });

      // Show toast notification
      toast.success(`🎉 Asset "${data.assetName || data.assetId}" assigned to you!`, {
        duration: 6000,
        icon: '🎉',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    }
  }

  handleAssetReturned(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleAssetReturned called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      // Add to Redux store
      store.dispatch(addNotification({
        type: 'info',
        title: 'Asset Returned',
        message: `Asset ${data.assetName || data.assetId} has been returned and is now available for reassignment.`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for asset returned');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Import assetApi to invalidate asset cache
      import('../services/assetApi').then(({ assetApi }) => {
        store.dispatch(assetApi.util.invalidateTags(['Asset']));
        console.log('✅ Asset cache invalidated for returned asset');
      });

      // Show toast notification
      toast(`📦 Asset "${data.assetName || data.assetId}" returned`, {
        duration: 5000,
        icon: '📦',
        style: {
          background: '#3B82F6',
          color: '#fff',
        },
      });
    }
  }

  handleAssetRequestApproved(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleAssetRequestApproved called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      console.log('✅ Asset request approved - updating UI');

      // Add to Redux store
      store.dispatch(addNotification({
        type: 'success',
        title: 'Asset Request Approved',
        message: 'Your asset request has been approved! You will be assigned an asset soon.'
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for asset request approved');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Import assetApi to invalidate asset requests cache
      import('../services/assetApi').then(({ assetApi }) => {
        console.log('🔄 Invalidating assetApi AssetRequest cache');
        store.dispatch(assetApi.util.invalidateTags([{ type: 'AssetRequest', id: 'LIST' }]));
        console.log('✅ Asset request cache invalidated - UI should update');

        // Also trigger refetch by updating the component's refresh counter
        // This ensures immediate UI update even if cache invalidation has timing issues
        if (window.location.pathname.includes('/employee/assets')) {
          console.log('🔄 Triggering component refresh for Asset Dashboard');
          // Dispatch a custom action that the component can listen to
          store.dispatch({ type: 'ASSET_REQUEST_REFRESH_TRIGGERED' });
        }
      });

      // Show toast notification
      toast.success('✅ Asset request approved! You will be assigned an asset soon.', {
        duration: 7000,
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    } else {
      console.log('❌ Asset request approved - not for this user');
    }
  }

  handleAssetRequestRejected(data) {
    const user = store.getState().auth.user;
    // Check for employeeId in different possible locations
    const currentUserEmployeeId = user?.employeeId || user?.employee?._id;

    console.log('🔍 handleAssetRequestRejected called', {
      eventEmployeeId: data.employeeId,
      currentUser: {
        userId: user?._id,
        userEmployeeId: user?.employeeId,
        employeeId: user?.employee?._id,
        resolvedEmployeeId: currentUserEmployeeId
      },
      idsMatch: currentUserEmployeeId === data.employeeId
    });

    if (currentUserEmployeeId === data.employeeId) {
      console.log('✅ Asset request rejected - updating UI');

      // Add to Redux store
      store.dispatch(addNotification({
        type: 'error',
        title: 'Asset Request Rejected',
        message: `Your asset request has been rejected. ${data.remarks ? `Reason: ${data.remarks}` : ''}`
      }));

      // Invalidate RTK Query cache to trigger refetch
      console.log('🔄 Invalidating notification cache for asset request rejected');
      store.dispatch(notificationApi.util.invalidateTags(['Notification']));

      // Import assetApi to invalidate asset requests cache
      import('../services/assetApi').then(({ assetApi }) => {
        console.log('🔄 Invalidating assetApi AssetRequest cache');
        store.dispatch(assetApi.util.invalidateTags([{ type: 'AssetRequest', id: 'LIST' }]));
        console.log('✅ Asset request cache invalidated - UI should update');
      });

      // Show toast notification
      toast.error(`Asset request rejected: ${data.remarks || 'No reason provided'}`, {
        duration: 8000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
    } else {
      console.log('❌ Asset request rejected - not for this user');
    }
  }

  // Emit events
  emitLeaveRequestSubmitted(data) {
    if (this.socket?.connected) {
      this.socket.emit('leave:request-submitted', data);
    } else {
      console.warn('Socket not connected, cannot emit leave request submitted event');
    }
  }

  emitLeaveRequestApproved(data) {
    if (this.socket?.connected) {
      this.socket.emit('leave:request-approved', data);
    } else {
      console.warn('Socket not connected, cannot emit leave request approved event');
    }
  }

  emitLeaveRequestRejected(data) {
    if (this.socket?.connected) {
      this.socket.emit('leave:request-rejected', data);
    } else {
      console.warn('Socket not connected, cannot emit leave request rejected event');
    }
  }

  emitLeaveRequestCancelled(data) {
    if (this.socket?.connected) {
      this.socket.emit('leave:request-cancelled', data);
    } else {
      console.warn('Socket not connected, cannot emit leave request cancelled event');
    }
  }

  emitAttendanceClockIn(data) {
    if (this.socket?.connected) {
      this.socket.emit('attendance:clock-in', data);
    } else {
      console.warn('Socket not connected, cannot emit attendance clock-in event');
    }
  }

  emitAttendanceClockOut(data) {
    if (this.socket?.connected) {
      this.socket.emit('attendance:clock-out', data);
    } else {
      console.warn('Socket not connected, cannot emit attendance clock-out event');
    }
  }

  emitNewHoliday(data) {
    if (this.socket?.connected) {
      this.socket.emit('holiday:new-holiday', data);
    } else {
      console.warn('Socket not connected, cannot emit new holiday event');
    }
  }

  emitHolidayUpdated(data) {
    if (this.socket?.connected) {
      this.socket.emit('holiday:holiday-updated', data);
    } else {
      console.warn('Socket not connected, cannot emit holiday updated event');
    }
  }

  emitLoanRequestSubmitted(data) {
    if (this.socket?.connected) {
      this.socket.emit('loan:request-submitted', data);
    } else {
      console.warn('Socket not connected, cannot emit loan request submitted event');
    }
  }

  emitLoanRequestApproved(data) {
    if (this.socket?.connected) {
      this.socket.emit('loan:request-approved', data);
    } else {
      console.warn('Socket not connected, cannot emit loan request approved event');
    }
  }

  emitLoanRequestRejected(data) {
    if (this.socket?.connected) {
      this.socket.emit('loan:request-rejected', data);
    } else {
      console.warn('Socket not connected, cannot emit loan request rejected event');
    }
  }

  emitLoanRequestCancelled(data) {
    if (this.socket?.connected) {
      this.socket.emit('loan:request-cancelled', data);
    } else {
      console.warn('Socket not connected, cannot emit loan request cancelled event');
    }
  }

  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      store.dispatch(setSocketConnected(false));
    } else {
      console.log('🔌 Socket already disconnected or null');
    }
  }

  getSocket() {
    return this.socket;
  }

  isSocketConnected() {
    return this.isConnected;
  }
}

// Create singleton instance
let socketServiceInstance = null;

const createSocketService = () => {
  if (!socketServiceInstance) {
    socketServiceInstance = new SocketService();
  }
  return socketServiceInstance;
};

// Export the singleton instance
const socketService = createSocketService();

export default socketService;
